import Fuse from "fuse.js";
const SEARCH_STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "can",
  "change",
  "configure",
  "edit",
  "for",
  "help",
  "i",
  "in",
  "load",
  "me",
  "my",
  "need",
  "of",
  "off",
  "on",
  "open",
  "please",
  "plz",
  "setting",
  "settings",
  "show",
  "the",
  "to",
  "turn",
  "update",
  "want",
  "with",
]);

export interface WeightedSearchKey {
  name: string;
  weight: number;
}

export interface WeightedSearchResult<TDocument> {
  document: TDocument;
  score: number;
}

export interface WeightedDocumentSearcher<TDocument> {
  search(query: string): WeightedSearchResult<TDocument>[];
}

/**
 * Builds the fuse index once for a document set. Callers with a static
 * document set should hold the searcher to avoid re-indexing per query.
 */
export function createWeightedDocumentSearcher<TDocument>({
  documents,
  keys,
}: {
  documents: readonly TDocument[];
  keys: readonly WeightedSearchKey[];
}): WeightedDocumentSearcher<TDocument> {
  const fuse = new Fuse(documents, {
    keys: [...keys],
    includeScore: true,
    ignoreLocation: true,
    minMatchCharLength: 1,
    shouldSort: true,
    threshold: 0.35,
  });

  return {
    search(query: string): WeightedSearchResult<TDocument>[] {
      const bestResultByDocument = new Map<
        TDocument,
        WeightedSearchResult<TDocument>
      >();
      const consider = (variant: string, penalty: number) => {
        for (const result of fuse.search(variant)) {
          const searchResult = {
            document: result.item,
            score: Math.min((result.score ?? 0) + penalty, 1),
          };
          const existing = bestResultByDocument.get(searchResult.document);
          if (!existing || searchResult.score < existing.score) {
            bestResultByDocument.set(searchResult.document, searchResult);
          }
        }
      };

      const variants = createSearchQueryVariants(query);
      for (const queryVariant of variants) {
        consider(queryVariant, 0);
      }
      /** Word windows rescue long conversational queries that defeat Fuse's
       *  whole-pattern matching; the penalty keeps them below organic matches. */
      for (const windowVariant of uniqueStrings(
        variants.flatMap(createWordWindowVariants),
      )) {
        consider(windowVariant, WORD_WINDOW_SCORE_PENALTY);
      }

      const tokens = getMeaningfulQueryTokens(query);
      return [...bestResultByDocument.values()]
        .filter((result) => documentMentionsAnyToken(result.document, tokens))
        .sort((a, b) => a.score - b.score);
    },
  };
}

export function searchWeightedDocuments<TDocument>({
  documents,
  keys,
  query,
}: {
  documents: readonly TDocument[];
  keys: readonly WeightedSearchKey[];
  query: string;
}): WeightedSearchResult<TDocument>[] {
  return createWeightedDocumentSearcher({ documents, keys }).search(query);
}

/** Fuzzy matching over long serialized fields scores short queries near-perfect
 *  against documents that never mention them, so relevance alone can't tell a
 *  real hit from noise. Require the document to actually contain one of the
 *  query's meaningful words. */
function getMeaningfulQueryTokens(query: string): string[] {
  return [
    ...new Set(
      query
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter((token) => token.length >= 3 && !SEARCH_STOP_WORDS.has(token)),
    ),
  ];
}

function documentMentionsAnyToken(
  document: unknown,
  tokens: readonly string[],
): boolean {
  if (tokens.length === 0) {
    return true;
  }
  const haystack = JSON.stringify(document)?.toLowerCase() ?? "";
  return tokens.some((token) => haystack.includes(token));
}

export function createSearchQueryVariants(query: string): string[] {
  const variants = [
    query,
    ...query.split(/[-–—:;,.!?]/).map((part) => part.trim()),
  ];

  return uniqueStrings(
    variants.flatMap((variant) => [variant, createKeywordQuery(variant)]),
  ).filter((variant) => variant.length > 0);
}

/** Above Fuse's 0.35 threshold, so penalized matches rank below organic ones. */
const WORD_WINDOW_SCORE_PENALTY = 0.4;

/** Word pairs and keywords from long (4+ word) queries; short queries are
 *  untouched so palette matching is unchanged. */
function createWordWindowVariants(query: string): string[] {
  const words = query.split(/\s+/).filter(Boolean);
  if (words.length < 4) {
    return [];
  }
  const windows: string[] = [];
  for (let i = 0; i < words.length - 1; i++) {
    windows.push(`${words[i]} ${words[i + 1]}`);
  }
  for (const word of words) {
    if (word.length >= 3 && !SEARCH_STOP_WORDS.has(word.toLowerCase())) {
      windows.push(word);
    }
  }
  return windows;
}

export function sanitizeSearchLimit(limit: number): number {
  if (!Number.isFinite(limit)) {
    return 0;
  }

  return Math.max(Math.floor(limit), 0);
}

export function toSearchRelevanceScore(fuseScore: number): number {
  const boundedScore = Math.min(Math.max(fuseScore, 0), 1);
  return Number((1 - boundedScore).toFixed(3));
}

export function stringifySearchContext(value: unknown): string | undefined {
  if (value === undefined) {
    return undefined;
  }

  return JSON.stringify(value);
}

function createKeywordQuery(query: string): string {
  return query
    .toLowerCase()
    .replace(/[^a-z0-9/_]+/g, " ")
    .split(/\s+/)
    .filter((word) => word && !SEARCH_STOP_WORDS.has(word))
    .join(" ");
}

function uniqueStrings(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    const normalized = value.trim();
    const key = normalized.toLowerCase();
    if (!normalized || seen.has(key)) {
      continue;
    }

    seen.add(key);
    result.push(normalized);
  }

  return result;
}
